/* src/utils/analyticsUtils.js */

// Import necessary helpers, if any (formatCombinedDifficulty is used for avgFocusLevel)
import { formatCombinedDifficulty } from '../contexts/gameHelpers'; // Adjust path if needed

// --- Constants for Controls ---

export const METRICS = {
  AVG_FOCUS_LEVEL: {
    key: 'avgFocusLevel',
    // --- >>> NAME CHANGED HERE <<< ---
    name: 'Average N-Back Level', // Changed from 'Average Focus Level'
    // --- >>> END CHANGE <<< ---
    // Formatter for the summary cards and potentially tooltips if not overridden
    formatter: (value) => (value !== null && value !== undefined && Number.isFinite(value) ? value.toFixed(2) : 'N/A'),
    aggregation: 'average', // Type of aggregation needed
  },
  AVG_DPRIME: {
    key: 'avgDprime',
    name: "Average Overall d'",
    formatter: (value) => (value !== null && value !== undefined && Number.isFinite(value) ? value.toFixed(2) : 'N/A'),
    aggregation: 'average',
  },
  VISUAL_FAR: {
    key: 'visualFAR', // Key used internally and expected in session data
    name: 'Visual False Alarm Rate',
    // Value is expected as a percentage (0-100) from session data
    formatter: (value) => (value !== null && value !== undefined && Number.isFinite(value) ? `${value.toFixed(1)}%` : 'N/A'),
    aggregation: 'average', // Average the FAR across sessions in the period
  },
  AUDIO_FAR: {
    key: 'audioFAR', // Key used internally and expected in session data
    name: 'Audio False Alarm Rate',
    // Value is expected as a percentage (0-100) from session data
    formatter: (value) => (value !== null && value !== undefined && Number.isFinite(value) ? `${value.toFixed(1)}%` : 'N/A'),
    aggregation: 'average', // Average the FAR across sessions in the period
  },
  TOTAL_PLAY_TIME: {
    key: 'totalPlayTime',
    name: 'Total Play Time',
    // Formatter shows human-readable time (e.g., 1h 15m, 25m 30s)
    formatter: (value) => { // value is expected in milliseconds
      // Handle null, undefined, non-finite, or zero/negative values
      if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) return '0s';

      const totalSeconds = Math.floor(value / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      let result = '';
      if (hours > 0) result += `${hours}h `;
      // Show minutes if > 0 OR if hours exist (even if minutes are 0)
      if (minutes > 0 || hours > 0) {
          result += `${minutes}m `;
      }
       // Show seconds if total time is less than a minute OR if there are seconds left over and total > 0
      if (totalSeconds > 0 && (totalSeconds < 60 || seconds > 0)) {
          result += `${seconds}s`;
      }

      // Trim trailing space if any
      result = result.trim();

      // Fallback if somehow result is empty (shouldn't happen with checks above)
      return result === '' ? '0s' : result;
    },
    aggregation: 'sum',
  },
  NUM_SESSIONS: {
    key: 'numSessions',
    name: 'Number of Sessions',
    formatter: (value) => (value !== null && value !== undefined && Number.isFinite(value) ? value : '0'),
    aggregation: 'count',
  },
  // Example for future:
  // AVG_ACCURACY: {
  //   key: 'avgAccuracy',
  //   name: 'Average Accuracy',
  //   formatter: (value) => (value !== null && value !== undefined && Number.isFinite(value) ? `${value.toFixed(1)}%` : 'N/A'),
  //   aggregation: 'average',
  // },
};

export const PERIODS = {
  LAST_7_DAYS: { key: 'last7', name: 'Last 7 Days', days: 7 },
  LAST_30_DAYS: { key: 'last30', name: 'Last 30 Days', days: 30 },
  THIS_MONTH: { key: 'thisMonth', name: 'This Month' }, // Requires special date logic
  LAST_MONTH: { key: 'lastMonth', name: 'Last Month' }, // Requires special date logic
  // CUSTOM: { key: 'custom', name: 'Custom Range' } // Add later if needed
};

// --- Helper Functions ---

/**
 * Calculates start/end dates for current and previous periods based on a key.
 * Handles 'lastN', 'thisMonth', 'lastMonth'.
 * Ensures dates are valid Date objects.
 *
 * @param {string} periodKey - Key from PERIODS (e.g., 'last7', 'thisMonth').
 * @param {Date} [referenceDate=new Date()] - The date to calculate relative to (usually today).
 * @returns {object|null} Object with { currentStart, currentEnd, previousStart, previousEnd } Dates or null if invalid period/calculation.
 */
export function calculateDateRanges(periodKey, referenceDate = new Date()) {
  // Ensure referenceDate is a valid Date object
  if (!(referenceDate instanceof Date) || isNaN(referenceDate)) {
      console.error("calculateDateRanges received an invalid referenceDate.");
      referenceDate = new Date(); // Fallback to current date
  }

  const endOfReferenceDay = new Date(referenceDate);
  endOfReferenceDay.setHours(23, 59, 59, 999); // End of the reference day

  let currentStart, currentEnd, previousStart, previousEnd;
  currentEnd = endOfReferenceDay; // Current period usually ends on the reference day

  const periodInfo = Object.values(PERIODS).find(p => p.key === periodKey);

  try {
    if (periodInfo && periodInfo.days) { // Handle 'lastN' days
      if (typeof periodInfo.days !== 'number' || periodInfo.days <= 0) {
          throw new Error(`Invalid 'days' property for period key: ${periodKey}`);
      }
      currentStart = new Date(currentEnd);
      currentStart.setDate(currentEnd.getDate() - periodInfo.days + 1);
      currentStart.setHours(0, 0, 0, 0); // Start of the day N days ago

      previousEnd = new Date(currentStart);
      previousEnd.setDate(currentStart.getDate() - 1); // End of the day before currentStart
      previousEnd.setHours(23, 59, 59, 999);

      previousStart = new Date(previousEnd);
      previousStart.setDate(previousEnd.getDate() - periodInfo.days + 1);
      previousStart.setHours(0, 0, 0, 0); // Start of the day N days before previousEnd

    } else if (periodKey === 'thisMonth') {
      currentStart = new Date(currentEnd.getFullYear(), currentEnd.getMonth(), 1); // First day of current month
      currentStart.setHours(0, 0, 0, 0);

      previousEnd = new Date(currentStart);
      previousEnd.setDate(currentStart.getDate() - 1); // Last day of previous month
      previousEnd.setHours(23, 59, 59, 999);

      previousStart = new Date(previousEnd.getFullYear(), previousEnd.getMonth(), 1); // First day of previous month
      previousStart.setHours(0, 0, 0, 0);

    } else if (periodKey === 'lastMonth') {
      const firstOfThisMonth = new Date(currentEnd.getFullYear(), currentEnd.getMonth(), 1);
      currentEnd = new Date(firstOfThisMonth); // End date is end of LAST month
      currentEnd.setDate(firstOfThisMonth.getDate() - 1); // Go to last day of previous month
      currentEnd.setHours(23, 59, 59, 999);

      currentStart = new Date(currentEnd.getFullYear(), currentEnd.getMonth(), 1); // Start date is start of LAST month
      currentStart.setHours(0, 0, 0, 0);

      previousEnd = new Date(currentStart); // End of month BEFORE last month
      previousEnd.setDate(currentStart.getDate() - 1);
      previousEnd.setHours(23, 59, 59, 999);

      previousStart = new Date(previousEnd.getFullYear(), previousEnd.getMonth(), 1); // Start of month BEFORE last month
      previousStart.setHours(0, 0, 0, 0);

    } else {
      throw new Error(`Invalid period key provided: ${periodKey}`);
    }

    // Final check to ensure all calculated dates are valid
    if (isNaN(currentStart) || isNaN(currentEnd) || isNaN(previousStart) || isNaN(previousEnd)) {
      throw new Error("Date calculation resulted in one or more invalid dates.");
    }

    // console.log(`Date Ranges [${periodKey}]: Current=${currentStart.toISOString().substring(0,10)} -> ${currentEnd.toISOString().substring(0,10)}, Previous=${previousStart.toISOString().substring(0,10)} -> ${previousEnd.toISOString().substring(0,10)}`); // Debug Log
    return { currentStart, currentEnd, previousStart, previousEnd };

  } catch (error) {
      console.error(`Error in calculateDateRanges for period '${periodKey}':`, error);
      return null; // Return null on any error
  }
}


/**
 * Filters an array of sessions to include only those whose 'date' property
 * falls within a given date range (inclusive start, inclusive end).
 * Handles invalid session dates gracefully.
 *
 * @param {Array<Object>} sessions - Array of session objects, each expected to have a 'date' property (ISO string).
 * @param {Date} startDate - The starting date of the desired range.
 * @param {Date} endDate - The ending date of the desired range.
 * @returns {Array<Object>} Filtered array of sessions. Returns empty array if inputs are invalid.
 */
export function filterSessionsByDate(sessions, startDate, endDate) {
  // Input validation
  if (!Array.isArray(sessions) || !(startDate instanceof Date) || !(endDate instanceof Date) || isNaN(startDate) || isNaN(endDate)) {
    // console.warn("Invalid input to filterSessionsByDate."); // Optional warning
    return [];
  }

  return sessions.filter(session => {
    // Check if session and session.date exist
    if (!session || !session.date) return false;

    try {
      const sessionDate = new Date(session.date);
      // Check if the parsed session date is valid and falls within the range
      return !isNaN(sessionDate) && sessionDate >= startDate && sessionDate <= endDate;
    } catch (e) {
      // Log error if date parsing fails for a specific session
      console.warn(`Could not parse date for session ID ${session.id || 'N/A'}:`, session.date, e);
      return false;
    }
  });
}

/**
 * Aggregates a specific metric over an array of sessions based on the metric's configuration.
 * Handles 'average', 'sum', and 'count' aggregations.
 * Now correctly handles 'visualFAR' and 'audioFAR'.
 *
 * @param {Array<Object>} sessions - Array of session objects for the period.
 * @param {string} metricKey - Key from METRICS (e.g., 'avgFocusLevel', 'totalPlayTime', 'visualFAR').
 * @returns {number|null} The aggregated value. Returns 0 for sum/count if no sessions, null for average if no valid sessions.
 */
export function aggregateMetric(sessions, metricKey) {
  const metricInfo = Object.values(METRICS).find(m => m.key === metricKey);

  // Return default values if metric config not found or no sessions
  if (!metricInfo || !Array.isArray(sessions) || sessions.length === 0) {
    return metricInfo?.aggregation === 'average' ? null : 0;
  }

  // Handle count directly
  if (metricInfo.aggregation === 'count') {
    return sessions.length;
  }

  let total = 0;
  let validCount = 0; // Count only sessions contributing valid numbers to the total

  sessions.forEach((session) => {
    let value = null; // Extracted value for the current session

    // Extract the correct value based on the metric key
    switch (metricKey) {
      case 'avgFocusLevel': // Key remains the same
        const nBack = session.nBack;
        const difficulty = session.difficulty ?? 0;
        if (typeof nBack === 'number' && typeof difficulty === 'number' && Number.isFinite(nBack) && Number.isFinite(difficulty)) {
            const clampedDiff = Math.max(0, Math.min(0.95, difficulty));
            value = nBack + clampedDiff;
        }
        break;
      case 'avgDprime':
        value = session.overallDprime;
        break;
      case 'visualFAR':
        value = session.visualFAR;
        break;
      case 'audioFAR':
        value = session.audioFAR;
        break;
      case 'totalPlayTime':
        value = session.durationMs;
        break;
      // case 'avgAccuracy': // Example for future
      //   value = session.accuracy;
      //   break;
      default:
        console.warn(`Unknown metric key encountered during aggregation: ${metricKey}`);
        return; // Skip this session
    }

    // Check if the extracted value is a valid number for sum/average calculation
    if (value !== null && value !== undefined && typeof value === 'number' && Number.isFinite(value)) {
      total += value;
      validCount++; // Increment count only if value is valid for averaging/summing
    }
  });

  // Calculate final aggregate based on type
  if (metricInfo.aggregation === 'sum') {
    return total; // Return the total sum
  } else if (metricInfo.aggregation === 'average') {
    // Return average only if there were valid sessions, otherwise null
    return validCount > 0 ? total / validCount : null;
  }

  // Fallback if aggregation type is unknown
  console.error(`Unknown aggregation type '${metricInfo.aggregation}' for metric '${metricKey}'`);
  return null;
}


/**
 * Prepares data structured for a line chart comparison, grouping by day.
 * Includes the actual Date object for each data point, suitable for time-based X-axis.
 *
 * @param {Array<Object>} currentSessions - Sessions filtered for the current period.
 * @param {Array<Object>} previousSessions - Sessions filtered for the previous period.
 * @param {string} metricKey - Key of the metric to plot (e.g., 'avgFocusLevel', 'visualFAR').
 * @param {Date} currentStartDate - The start Date object of the current period.
 * @param {number} daysInPeriod - The total number of days the period spans.
 * @returns {Array<Object>} Array of objects formatted for charting,
 *                          e.g., [{ date: DateObject, name: 'Day 1', current: val1, previous: val2 }, ...]
 *                          Returns empty array if inputs are invalid.
 */
export function prepareChartData(currentSessions, previousSessions, metricKey, currentStartDate, daysInPeriod) {
  // Input validation
  if (!Array.isArray(currentSessions) || !Array.isArray(previousSessions) || !metricKey || !(currentStartDate instanceof Date) || isNaN(currentStartDate) || typeof daysInPeriod !== 'number' || daysInPeriod <= 0) {
      console.error("Invalid input provided to prepareChartData.");
      return [];
  }

  const metricInfo = Object.values(METRICS).find(m => m.key === metricKey);
  if (!metricInfo) {
      console.error(`Metric configuration not found for key: ${metricKey}`);
      return [];
  }

  const chartData = [];
  const aggregationType = metricInfo.aggregation;

  // Loop through each day within the period length
  for (let i = 0; i < daysInPeriod; i++) {
    // Calculate date range for the specific day (Day i+1) in the CURRENT period
    const currentDayDate = new Date(currentStartDate);
    currentDayDate.setDate(currentStartDate.getDate() + i);
    const currentDayStart = new Date(currentDayDate); currentDayStart.setHours(0, 0, 0, 0);
    const currentDayEnd = new Date(currentDayDate); currentDayEnd.setHours(23, 59, 59, 999);

    // Calculate date range for the corresponding day in the PREVIOUS period
    const previousDayStart = new Date(currentDayStart); previousDayStart.setDate(currentDayStart.getDate() - daysInPeriod); previousDayStart.setHours(0, 0, 0, 0);
    const previousDayEnd = new Date(previousDayStart); previousDayEnd.setHours(23, 59, 59, 999);

    // Filter sessions that fall exactly on this specific day
    const currentDaySessions = filterSessionsByDate(currentSessions, currentDayStart, currentDayEnd);
    const previousDaySessions = filterSessionsByDate(previousSessions, previousDayStart, previousDayEnd);

    // Aggregate the metric for the sessions found on this specific day
    // aggregateMetric handles the logic for all metrics, including the new FAR ones
    let currentValue = aggregateMetric(currentDaySessions, metricKey);
    let previousValue = aggregateMetric(previousDaySessions, metricKey);

    // Handle null/zero values based on metric type for clearer charting
    if (aggregationType !== 'average') { // For sum or count
       currentValue = currentValue ?? 0;
       previousValue = previousValue ?? 0;
    }
    // For averages (like FAR, d', focus level), null is kept if no valid sessions exist

    // Add the data point for this day to the chart data array
    chartData.push({
      date: currentDayDate, // Include the actual Date object for X-axis formatting
      name: `Day ${i + 1}`, // Relative day name (useful for tooltips/debugging)
      current: currentValue,  // Aggregated value for the current period's day
      previous: previousValue, // Aggregated value for the previous period's corresponding day
    });
  }

  return chartData; // Return the array of daily data points
}